import { useMemo } from 'react';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { clipFilterPreviewStyle } from '@/lib/videoFilters';
import { VideoFilterOverlayStack } from '@/components/VideoFilterOverlayStack';

/**
 * Live filter preview for clip editor — CSS filters on a wrapper (not on <video> directly)
 * plus gradient overlays so effects are visible in the Edit Clip dialog.
 */
export function ClipFilterMediaPreview({
  clip,
  filters,
  className = '',
  mediaClassName = 'w-full h-full',
  fit = 'cover',
  videoProps = {},
}) {
  const filterStyle = useMemo(() => {
    const base = clipFilterPreviewStyle(filters);
    if (!base) return undefined;
    return {
      ...base,
      WebkitFilter: base.filter,
      transform: 'translateZ(0)',
    };
  }, [filters]);

  const objectClass = fit === 'contain' ? 'object-contain' : 'object-cover';
  const videoSrc = clip?.videoUrl ? mediaUrlForPlayback(clip.videoUrl) : null;
  const imageSrc = !videoSrc && clip?.imageUrl ? mediaUrlForPlayback(clip.imageUrl) : null;

  if (!videoSrc && !imageSrc) return null;

  return (
    <div className={`relative isolate overflow-hidden ${className}`.trim()}>
      <div className="absolute inset-0 z-0" style={filterStyle}>
        {videoSrc ? (
          <video
            src={videoSrc}
            className={`absolute inset-0 ${mediaClassName} ${objectClass}`}
            {...videoProps}
          />
        ) : (
          <img
            src={imageSrc}
            alt={clip?.name || 'Clip preview'}
            className={`absolute inset-0 ${mediaClassName} ${objectClass}`}
          />
        )}
      </div>
      <VideoFilterOverlayStack filters={filters} className="z-[12]" />
    </div>
  );
}
